import OrganizationSwitcher from "@/components/organization/OrganizationSwitcher";
import { ThemeToggle } from "@/components/theme/theme-toggle";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { featureTemplateService } from "@/services/featureTemplateService";
import { FeatureTemplateWithDetails } from "@/types/featureTemplate";
import {
  Activity,
  Bot,
  Calendar,
  CreditCard,
  FileJson,
  FileUp,
  LayoutDashboard,
  Lock,
  LogOut,
  Menu,
  MessageSquareText,
  Phone,
  Play,
  Plus,
  Settings,
  Sparkles,
  Users,
  UserCircle,
  Workflow,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../supabase/auth";
import { useOrganization } from "../../../App";
import { FeatureGuard } from "./FeatureGuard";
import PageHeader from "./PageHeader";

interface ActionButtonConfig {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  className?: string;
}

interface DashboardLayoutProps {
  children: React.ReactNode;
  activeItem?: string;
  showCreateButton?: boolean;
  title?: string;
  description?: string;
  actionButtons?: ActionButtonConfig[];
}

const DashboardLayout = ({
  children,
  activeItem = "Dashboard",
  showCreateButton = false,
  title,
  description,
  actionButtons = [],
}: DashboardLayoutProps) => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [templates, setTemplates] = useState<FeatureTemplateWithDetails[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  const { currentOrganization } = useOrganization();
  const isPro = currentOrganization?.is_pro || false;

  // Get the current path to determine active item
  const currentPath = window.location.pathname;

  // Storage key for templates cache
  const getCacheKey = () => currentOrganization ? `templates_${currentOrganization.id}` : null;
  
  // Clean up stale cache entries
  const cleanupStaleCache = () => {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('templates_')) {
          const data = localStorage.getItem(key);
          if (data) {
            const { timestamp } = JSON.parse(data);
            // Remove cache entries older than 1 hour
            if (Date.now() - timestamp > 60 * 60 * 1000) {
              localStorage.removeItem(key);
            }
          }
        }
      });
    } catch (error) {
      console.error("Error cleaning up cache:", error);
    }
  };

  // Initialize templates from localStorage on mount
  useEffect(() => {
    // Clean up old cache entries on mount
    cleanupStaleCache();
    
    if (currentOrganization) {
      const cacheKey = getCacheKey();
      if (cacheKey) {
        const cachedData = localStorage.getItem(cacheKey);
        if (cachedData) {
          try {
            const { templates: cachedTemplates, timestamp } = JSON.parse(cachedData);
            // Use cached data if it's less than 5 minutes old
            const isExpired = Date.now() - timestamp > 5 * 60 * 1000;
            if (!isExpired) {
              setTemplates(cachedTemplates);
            }
          } catch (error) {
            console.error("Error parsing cached templates:", error);
          }
        }
      }
    }
  }, [currentOrganization]);

  useEffect(() => {
    if (user && currentOrganization) {
      fetchTemplates();
    }
  }, [user, currentOrganization]);

  const fetchTemplates = async () => {
    if (!currentOrganization) return;

    // Don't show loading state if we have cached data
    const cacheKey = getCacheKey();
    const hasCachedData = cacheKey && localStorage.getItem(cacheKey);
    
    if (!hasCachedData) {
      setIsLoadingTemplates(true);
    }
    
    try {
      const data = await featureTemplateService.getFeatureTemplates(
        currentOrganization.id,
      );
      setTemplates(data);
      
      // Cache the templates in localStorage
      if (cacheKey) {
        localStorage.setItem(cacheKey, JSON.stringify({
          templates: data,
          timestamp: Date.now()
        }));
      }
    } catch (error) {
      console.error("Error fetching feature templates:", error);
    } finally {
      if (!hasCachedData) {
        setIsLoadingTemplates(false);
      }
    }
  };

  const allNavItems = [
    {
      icon: <LayoutDashboard size={20} />,
      label: "Dashboard",
      href: "/dashboard",
      requiresPro: false,
    },
    {
      icon: <Bot size={20} />,
      label: "Assistants",
      href: "/assistants",
      requiresPro: false,
    },
    {
      icon: <UserCircle size={20} />,
      label: "Personas",
      href: "/personas",
      requiresPro: false,
    },
    {
      icon: <Play size={20} />,
      label: "Simulators",
      href: "/simulators",
      requiresPro: false,
    },
    {
      icon: <MessageSquareText size={20} />,
      label: "Prompt Engine",
      href: "/prompt-engine",
      requiresPro: false,
    },
    {
      icon: <Sparkles size={20} />,
      label: "Micro Prompts",
      href: "/micro-prompts",
      requiresPro: false,
    },
    {
      icon: <Users size={20} />,
      label: "Leads",
      href: "/leads",
      requiresPro: false,
    },
    {
      icon: <FileUp size={20} />,
      label: "Lead Uploads",
      href: "/lead-uploads",
      requiresPro: false,
    },
    {
      icon: <Settings size={20} />,
      label: "Providers",
      href: "/providers",
      requiresPro: false,
    },
    {
      icon: <CreditCard size={20} />,
      label: "Credits Balance",
      href: "/credits-balance",
      requiresPro: false,
    },
    {
      icon: <Calendar size={20} />,
      label: "Analytics",
      href: "/analytics",
      requiresPro: true,
    },
    {
      icon: <Phone size={20} />,
      label: "Phone Numbers",
      href: "/phone-numbers",
      requiresPro: true,
    },
    {
      icon: <Phone className="rotate-180" size={20} />,
      label: "Call Requests",
      href: "/call-requests",
      requiresPro: false,
    },
    {
      icon: <FileJson size={20} />,
      label: "Schema Templates",
      href: "/schema-templates",
      requiresPro: false,
    },
    {
      icon: <MessageSquareText size={20} />,
      label: "Call Logs",
      href: "/call-logs",
      requiresPro: false,
    },
    {
      icon: <Activity size={20} />,
      label: "Latency Analytics",
      href: "/analytics/latency",
      requiresPro: false,
    },
    {
      icon: <Calendar size={20} />,
      label: "Scheduled Calls",
      href: "/scheduled-calls",
      requiresPro: false,
    },
    {
      icon: <Workflow size={20} />,
      label: "CallFlows",
      href: "/callflows",
      requiresPro: false,
    },
    {
      icon: <MessageSquareText size={20} />,
      label: "Slots",
      href: "/slots",
      requiresPro: false,
    },
    {
      icon: <FileJson size={20} />,
      label: "Feature Templates",
      href: "/feature-templates",
      requiresPro: false,
    },
  ];

  // Combine static nav items with dynamic feature templates
  const navItems = [
    ...allNavItems,
    // Add a separator item if we have templates
    ...(templates.length > 0 ? [{ isSeparator: true }] : []),
    // Add feature template items
    ...templates.map((template) => ({
      icon: <FileJson size={20} />,
      label: template.name,
      href: `/u/${template.name}`,
      requiresPro: false,
      isTemplate: true,
    })),
  ];

  const handleNavItemClick = (label: string) => {
    const item = navItems.find(
      (item) => !('isSeparator' in item) && item.label === label,
    );
    if (item && 'href' in item) {
      navigate(item.href);
    }
  };

  const handleCreateAssistant = () => {
    navigate("/assistant-dashboard");
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex transition-colors duration-300">
      <aside className="w-64 lg:w-64 max-lg:w-20 fixed top-0 left-0 h-screen bg-card border-r border-border flex flex-col transition-all duration-300 rounded-none">
        <div className="p-4 border-b border-border">
          <h1 className="text-xl font-bold text-primary mb-3 max-lg:text-center">VOXI</h1>
          <div className="max-lg:hidden">
            <OrganizationSwitcher />
          </div>
        </div>
        <nav className="flex flex-col p-2 flex-1 overflow-y-auto">
          {isLoadingTemplates && templates.length === 0 ? (
            <div className="flex justify-center py-4">
              <LoadingSpinner size="sm" />
            </div>
          ) : (
            navItems.map((item, index) =>
              ('isSeparator' in item) ? (
                <Separator key={`sep-${index}`} className="my-2 max-lg:my-1" />
              ) : (
                <FeatureGuard
                  key={'label' in item ? item.label : `nav-${index}`}
                  featureId={
                    ('isTemplate' in item && item.isTemplate)
                      ? "featuretemplates"
                      : ('label' in item ? item.label.toLowerCase().replace(/\s+/g, "") : '')
                  }
                  fallback={
                    <Button
                      variant="ghost"
                      className={cn(
                        "justify-start mb-1 py-3 text-muted-foreground opacity-60 cursor-not-allowed rounded-none",
                        "max-lg:flex-col max-lg:justify-center max-lg:py-2 max-lg:px-1 max-lg:h-auto"
                      )}
                      disabled
                    >
                      <div className="max-lg:text-2xl">{'icon' in item ? item.icon : null}</div>
                      <span className="ml-2 max-lg:ml-0 max-lg:text-xs max-lg:mt-1 max-lg:text-center max-lg:line-clamp-2">
                        {'label' in item ? item.label : ''}
                      </span>
                      <Lock className="ml-2 h-3 w-3 max-lg:hidden" />
                    </Button>
                  }
                >
                  <Button
                    variant="ghost"
                    className={cn(
                      "justify-start mb-1 py-3 rounded-none",
                      "max-lg:flex-col max-lg:justify-center max-lg:py-2 max-lg:px-1 max-lg:h-auto",
                      ('label' in item && item.label === activeItem) ||
                        ('isTemplate' in item && item.isTemplate && 'label' in item &&
                          currentPath.startsWith(`/u/${item.label}`))
                        ? "text-primary bg-primary/10"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent",
                      ('isTemplate' in item && item.isTemplate) && "text-sm font-normal",
                    )}
                    onClick={() => 'label' in item && handleNavItemClick(item.label)}
                  >
                    <div className="max-lg:text-2xl">{item.icon}</div>
                    <span className="ml-2 max-lg:ml-0 max-lg:text-xs max-lg:mt-1 max-lg:text-center max-lg:line-clamp-2">
                      {item.label}
                    </span>
                  </Button>
                </FeatureGuard>
              ),
            )
          )}
        </nav>
        <div className="p-4 border-t border-border flex justify-between items-center max-lg:flex-col max-lg:gap-2">
          <ThemeToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-foreground hover:bg-accent w-full justify-start max-lg:justify-center"
              >
                <Menu className="h-5 w-5 mr-2 max-lg:mr-0" />
                <span className="max-lg:hidden">Menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-none">
              <DropdownMenuItem onClick={() => window.location.reload()}>
                Refresh
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/team")}>
                <Users className="h-4 w-4 mr-2" />
                <span>Team</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate("/organization-settings")}
              >
                <Settings className="h-4 w-4 mr-2" />
                <span>Settings</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={signOut} className="text-destructive">
                <LogOut className="h-4 w-4 mr-2" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <main className="flex-1 py-6 px-4 md:px-6 ml-64 max-lg:ml-20 overflow-y-auto bg-white dark:bg-black transition-all duration-300">
        {(title || description) && (
          <PageHeader
            title={title || activeItem}
            description={description}
            showCreateButton={showCreateButton}
            createButtonLabel="Create Assistant"
            onCreateClick={handleCreateAssistant}
            actionButtons={actionButtons}
          />
        )}

        {showCreateButton && !title && (
          <Button
            onClick={handleCreateAssistant}
            className="bg-primary hover:bg-primary/80 text-primary-foreground font-medium md:hidden w-full mb-6"
          >
            <Plus className="mr-2 h-4 w-4" />
            Create Assistant
          </Button>
        )}

        {children}
      </main>
    </div>
  );
};

export default DashboardLayout;